import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, User, SignOut, Package, ArrowClockwise, Receipt, BookmarkSimple, Plus, Trash, Check } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import { customerLogin, customerRegister, getCustomerOrders, getSavedLists, createSavedList, deleteSavedList, removeFromSavedList } from "@/lib/api";

const field =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-navy-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-600 transition";
const label = "block text-sm font-semibold text-navy-900 mb-1.5";

const fmtErr = (d) => {
  if (!d) return "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return d?.msg || "Please try again.";
};

export default function Account() {
  const navigate = useNavigate();
  const { user, ready, token, login, logout } = useAuth();
  const { addItem } = useCart();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ email: "", password: "", name: "", company: "" });
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [lists, setLists] = useState([]);
  const [newListName, setNewListName] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const loadLists = () => { if (token) getSavedLists(token).then(setLists).catch(() => setLists([])); };
  useEffect(() => {
    if (user && token) { getCustomerOrders(token).then(setOrders).catch(() => setOrders([])); loadLists(); }
  }, [user, token]);

  const addList = async () => {
    if (!newListName.trim()) return;
    await createSavedList(token, newListName.trim());
    setNewListName("");
    loadLists();
    toast.success("List created.");
  };
  const removeList = async (id) => { await deleteSavedList(token, id); loadLists(); };
  const removeItem = async (listId, pid) => { await removeFromSavedList(token, listId, pid); loadLists(); };
  const addListToCart = (list) => {
    list.products.forEach((p) => addItem(p, Math.max(1, p.min_order_qty || 1)));
    toast.success(`Added ${list.products.length} item(s) to cart.`);
    navigate("/supply/cart");
  };

  const reorder = (order) => {
    order.items.forEach((it) => {
      addItem({ id: it.product_id, name: it.name, images: [], price: it.unit_price, unit: "", tiered_pricing: [] }, it.quantity);
    });
    toast.success("Added to cart. Review and checkout.");
    navigate("/supply/cart");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = mode === "login"
        ? await customerLogin({ email: f.email, password: f.password })
        : await customerRegister({ email: f.email, password: f.password, name: f.name, company: f.company });
      login(data.token, data.customer);
      toast.success(mode === "login" ? "Welcome back!" : "Account created!");
      navigate("/supply");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (ready && user) {
    return (
      <div className="pt-28 md:pt-32 min-h-[70vh]" data-testid="account-page">
        <div className="max-w-[700px] mx-auto px-5 md:px-10 py-10">
          <Link to="/supply" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-navy-900 mb-6"><ArrowLeft size={16} /> Back to AMWAP Supply</Link>
          <div className="bg-white border border-slate-200 rounded-3xl p-8">
            <div className="w-14 h-14 rounded-full bg-navy-900 text-white grid place-items-center"><User size={26} weight="fill" /></div>
            <h1 className="font-display font-black text-3xl text-navy-900 mt-4">Hi, {user.name?.split(" ")[0] || "there"}.</h1>
            <p className="text-slate-500 mt-1">{user.email}{user.company ? ` · ${user.company}` : ""}</p>
            <div className="flex flex-wrap gap-3 mt-6">
              <Link to="/supply" data-testid="account-view-scoped" className="inline-flex items-center gap-2 rounded-full bg-navy-900 text-white px-6 py-3 font-bold hover:bg-navy-600 transition-colors"><Package size={18} weight="fill" /> View your scoped items</Link>
              <button onClick={() => { logout(); toast.success("Signed out."); }} data-testid="account-logout" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 py-3 font-bold text-navy-900 hover:bg-slate-100"><SignOut size={18} /> Sign out</button>
            </div>
          </div>

          {/* Order history */}
          <div className="mt-8" data-testid="order-history">
            <h2 className="font-display font-black text-2xl text-navy-900 tracking-tight flex items-center gap-2"><Receipt size={24} weight="fill" className="text-navy-600" /> Past orders</h2>
            {orders.length === 0 ? (
              <p className="text-slate-500 mt-3" data-testid="no-orders">No orders yet. Once you buy, your orders show here for fast reordering.</p>
            ) : (
              <div className="space-y-3 mt-4">
                {orders.map((o, i) => {
                  const STEPS = ["paid", "packed", "shipped", "delivered"];
                  const st = o.status || "paid";
                  const cur = STEPS.indexOf(st);
                  const cancelled = st === "cancelled";
                  return (
                  <div key={o.id} data-testid={`order-${i}`} className="bg-white border border-slate-200 rounded-2xl p-5">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-display font-bold text-navy-900">${Number(o.amount).toFixed(2)} <span className={`text-xs font-semibold ml-1 capitalize ${cancelled ? "text-slate-400" : "text-green-600"}`}>{st}</span></p>
                        <p className="text-xs text-slate-400 mt-0.5">{new Date(o.created_at).toLocaleDateString()} · {o.items.length} item{o.items.length !== 1 ? "s" : ""}{o.tracking ? ` · Tracking ${o.tracking}` : ""}</p>
                      </div>
                      <button onClick={() => reorder(o)} data-testid={`reorder-${i}`} className="inline-flex items-center gap-2 rounded-full bg-navy-900 text-white px-5 py-2.5 text-sm font-bold hover:bg-navy-600 transition-colors shrink-0"><ArrowClockwise size={16} weight="bold" /> Reorder</button>
                    </div>
                    {/* Live status tracker */}
                    {cancelled ? (
                      <p className="mt-4 text-sm font-semibold text-slate-400">This order was cancelled.</p>
                    ) : (
                      <div className="flex items-center mt-4" data-testid={`order-tracker-${i}`}>
                        {STEPS.map((s, k) => {
                          const done = k <= cur;
                          return (
                            <div key={s} className="flex items-center flex-1 last:flex-none">
                              <div className="flex flex-col items-center">
                                <div className={`w-7 h-7 rounded-full grid place-items-center ${done ? "bg-navy-900 text-white" : "bg-slate-100 text-slate-400"}`}>
                                  {done ? <Check size={14} weight="bold" /> : <span className="text-[11px] font-bold">{k + 1}</span>}
                                </div>
                                <span className={`mt-1 text-[10px] font-bold uppercase tracking-wide ${done ? "text-navy-900" : "text-slate-400"}`}>{s}</span>
                              </div>
                              {k < STEPS.length - 1 && <div className={`h-1 flex-1 mx-1.5 rounded ${k < cur ? "bg-navy-900" : "bg-slate-100"}`} />}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                      {o.items.map((it, k) => <span key={k}>{it.quantity} × {it.name}</span>)}
                    </div>
                  </div>
                );})}
              </div>
            )}
          </div>

          {/* Saved lists */}
          <div className="mt-8" data-testid="saved-lists">
            <h2 className="font-display font-black text-2xl text-navy-900 tracking-tight flex items-center gap-2"><BookmarkSimple size={24} weight="fill" className="text-navy-600" /> Saved lists</h2>
            <div className="flex gap-2 mt-4">
              <input data-testid="new-list-name" value={newListName} onChange={(e) => setNewListName(e.target.value)} placeholder="e.g. Monthly restock" className="flex-1 rounded-xl border border-slate-300 px-4 py-2.5 text-navy-900 focus:outline-none focus:ring-2 focus:ring-navy-600" />
              <button onClick={addList} data-testid="create-list" className="inline-flex items-center gap-1.5 rounded-full bg-navy-900 text-white px-5 py-2.5 font-bold hover:bg-navy-600 transition-colors"><Plus size={16} weight="bold" /> New list</button>
            </div>
            {lists.length === 0 ? (
              <p className="text-slate-500 mt-4" data-testid="no-lists">No saved lists yet. Create one, then tap "Save" on any product to add it.</p>
            ) : (
              <div className="space-y-4 mt-4">
                {lists.map((l, i) => (
                  <div key={l.id} data-testid={`list-${i}`} className="bg-white border border-slate-200 rounded-2xl p-5">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-display font-bold text-navy-900">{l.name} <span className="text-sm font-normal text-slate-400">({l.products.length})</span></h3>
                      <div className="flex items-center gap-2">
                        {l.products.length > 0 && <button onClick={() => addListToCart(l)} data-testid={`list-add-cart-${i}`} className="inline-flex items-center gap-1.5 rounded-full bg-navy-900 text-white px-4 py-2 text-sm font-bold hover:bg-navy-600"><ArrowClockwise size={15} weight="bold" /> Add all to cart</button>}
                        <button onClick={() => removeList(l.id)} data-testid={`list-delete-${i}`} aria-label="Delete list" className="w-8 h-8 grid place-items-center rounded-full border border-red-300 text-red-600 hover:bg-red-500 hover:text-white"><Trash size={15} /></button>
                      </div>
                    </div>
                    {l.products.length > 0 && (
                      <div className="mt-3 space-y-1.5">
                        {l.products.map((p) => {
                          const saves = p.list_price != null && p.list_price > (p.price || 0);
                          return (
                            <div key={p.id} className="flex items-center justify-between gap-3 text-sm">
                              <Link to={`/supply/${p.id}`} className="text-navy-900 hover:underline truncate flex-1">{p.name}</Link>
                              <div className="flex items-center gap-2 shrink-0">
                                {p.price != null && (
                                  <span className="flex items-center gap-1.5" data-testid={`list-price-${p.id}`}>
                                    {saves && <span className="text-xs text-slate-400 line-through">${Number(p.list_price).toFixed(2)}</span>}
                                    <span className={`font-bold ${saves ? "text-green-600" : "text-navy-900"}`}>${Number(p.price).toFixed(2)}</span>
                                    {saves && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">Your price</span>}
                                  </span>
                                )}
                                <button onClick={() => removeItem(l.id, p.id)} aria-label="Remove" className="text-slate-400 hover:text-red-600"><Trash size={14} /></button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-28 md:pt-32 min-h-[70vh]" data-testid="account-page">
      <div className="max-w-[480px] mx-auto px-5 md:px-10 py-10">
        <Link to="/supply" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-navy-900 mb-6"><ArrowLeft size={16} /> Back to AMWAP Supply</Link>
        <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-[0_12px_40px_rgb(10,17,40,0.06)]">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-navy-500">AMWAP Supply</span>
          <h1 className="font-display font-black text-3xl text-navy-900 tracking-tight mt-2">{mode === "login" ? "Sign in" : "Create account"}</h1>
          <p className="text-slate-500 mt-2 text-sm">{mode === "login" ? "Access your scoped items and faster checkout." : "For business customers — see the items scoped just for you."}</p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="account-form">
            {mode === "register" && (
              <>
                <div><label className={label}>Full name</label><input required data-testid="acct-name" className={field} value={f.name} onChange={(e) => set("name", e.target.value)} /></div>
                <div><label className={label}>Company (optional)</label><input data-testid="acct-company" className={field} value={f.company} onChange={(e) => set("company", e.target.value)} /></div>
              </>
            )}
            <div><label className={label}>Email</label><input required type="email" data-testid="acct-email" className={field} value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
            <div><label className={label}>Password</label><input required type="password" minLength={6} data-testid="acct-password" className={field} value={f.password} onChange={(e) => set("password", e.target.value)} placeholder={mode === "register" ? "At least 6 characters" : ""} /></div>
            <button type="submit" disabled={loading} data-testid="acct-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy-900 text-white py-3.5 font-bold hover:bg-navy-600 transition-colors disabled:opacity-60">
              {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"} <ArrowUpRight weight="bold" />
            </button>
          </form>

          <p className="text-sm text-slate-500 mt-5 text-center">
            {mode === "login" ? "New here? " : "Already have an account? "}
            <button data-testid="acct-toggle" onClick={() => setMode(mode === "login" ? "register" : "login")} className="font-bold text-navy-900 hover:underline">
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
